import ky from 'ky';
import { defaultTo, pick } from 'ramda';
import { dotPath } from 'ramda-extension';

import { sessionRemoval } from '../../store/session/actions';
import { getEnvironment, isServer, authServiceUrl } from '../utils';
import { LOGIN_RETURN_PATH_KEY } from '../session-storage-keys';
import { clearLoggedUser, logger, logAuthSession } from '../logger';

import { parseNetworkErrorResponse } from './parse-network-error-response';
import { switchAuthCookieToPublicDomain } from './auth-cookies';

const { WB_AUTH_SERVICE_PRE_REGISTER_PATH } = getEnvironment();
const HUB_APP_NAME = 'hub';

const captchaTokenField = (captchaType) =>
  captchaType === 'turnstile' ? 'turnstile-token' : 'g-recaptcha-response';

export const DEFAULT_AUTH_HEADERS = {
  'X-Requested-With': 'MobileApp',
  'X-Web-App': HUB_APP_NAME,
};

const MSG_NETWORK_ERROR =
  'Network Error: Please check your internet connection and try again.';

/**
 * Creates a new instance of the user service API for authentication requests.
 *
 * @exports
 * @param {object} options - This is merged with the axios config. Properties
 *   provided here override those set here.
 * @returns {object} An instance of the API.
 */
export const createService = (options = {}) =>
  ky.extend({
    prefixUrl: authServiceUrl,
    credentials: 'include',
    headers: DEFAULT_AUTH_HEADERS,
    responseType: 'json',
    ...options,
  });

const serviceSingleton = createService();

/**
 * The response to a login request.
 *
 * @typedef {null|object} LoginResponse
 * @property {number} id
 * @property {string} email
 * @property {string} username
 * @property {string} firstName
 * @property {string} lastName
 */

/**
 * Attempts to log a user in. Failures are silently logged.
 *
 * @exports
 * @param {object} options - The parameters for the request.
 * @param {string} options.email - The email of the user attempting to log in.
 * @param {string} options.password - The password for the user.
 * @param {object} [options.api] - Instance of API to user. Defaults to.
 * @param {string} options.captchaToken
 * @param {"recaptcha" | "turnstile"} [options.captchaType="recaptcha"]
 * @param {string} [options.product="waybetter"] -
 * singleton.
 * @returns {object} Returns the user profile on success or the error on failure.
 */
export async function login({
  email,
  password,
  api = serviceSingleton,
  captchaToken,
  captchaType = 'recaptcha',
  product = 'waybetter',
}) {
  try {
    const user = await api
      .post('auth/login', {
        json: {
          email,
          password,
          [captchaTokenField(captchaType)]: captchaToken,
        },
        headers: {
          ...DEFAULT_AUTH_HEADERS,
          'X-Product': product,
        },
      })
      .json();
    return { user };
  } catch (error) {
    logger.info(error, { message: 'Failed to authenticate user using email.' });
    if (isNetworkException(error)) {
      const { message } = buildHumanReadableNetworkException();
      return { error: message };
    }

    const errorBody = await error.response?.json();
    const message = dotPath('error.message', errorBody);

    return { error: message };
  }
}

/**
 * Attempts to log a user in with an auth token. This is the only login
 * method that's also called server-side, from route guards (see
 * lib/routes/guards.js) - a token URL can be the very first request the
 * server ever sees (e.g. a mobile deep link opening a fresh browser tab),
 * so without `ctx` here the auth service's Set-Cookie response would just
 * be discarded: a plain client-side fetch never happens, and the browser
 * never receives the session cookie despite the login call succeeding.
 *
 * @exports
 * @param {object} options - The parameters for the request.
 * @param {string} options.token - The auth token to log in with.
 * @param {object} [options.api] - Instance of API to user. Defaults to.
 * @param {string} [options.product="waybetter"] -
 * singleton.
 * @param {object} [options.ctx] - Koa server context - when present (i.e.
 *   this call is happening server-side), the auth service's Set-Cookie
 *   response headers are copied onto it, same as preRegister does below.
 * @returns {object} Returns the user profile on success or the error on failure.
 */
export async function authTokenLogin({
  token,
  api = serviceSingleton,
  product = 'waybetter',
  ctx,
}) {
  try {
    const resp = await api.post('auth/login/token', {
      json: {
        token,
      },
      headers: {
        ...DEFAULT_AUTH_HEADERS,
        'X-Product': product,
      },
    });

    if (ctx) {
      const respCookies = responseCookies(resp);
      logAuthSession(`Received auth token login cookies ${respCookies}`);
      const cookies = switchAuthCookieToPublicDomain(ctx, respCookies);
      cookies.forEach((cookie) => ctx.response.append('set-cookie', cookie));
    }

    const user = await resp.json();
    return { user };
  } catch (error) {
    logger.info(error, { message: 'Failed to authenticate user using token.' });
    if (isNetworkException(error)) {
      const { message } = buildHumanReadableNetworkException();
      return { error: message };
    }

    const errorBody = await error.response?.json();
    const message = dotPath('error.message', errorBody);

    return { error: message };
  }
}

/**
 * Calls to the auth service to {@link https://developer.apple.com/documentation/sign_in_with_apple/sign_in_with_apple_rest_api/verifying_a_user|perform a verification of the Apple ID token},
 * and creates a user if verification succeeds.
 *
 * @exports
 * @param {object} options - The parameters for the request.
 * @param {object} [options.api] - Instance of API to user. Defaults to
 *   singleton.
 * @param {string} options.authorizationCode - The authorization code obtained via the Apple client-side API.
 * @param {string} options.idToken - The ID token obtained via the Apple client-side API.
 * @param {string} options.product - The product this login is for.
 * @param {string} options.nonce - The nonce initially passed to the Apple client-side API.
 * @param {string} [options.prospectPhoneNumber] - The Supercharger pay flow's
 *   number (E.164): a new login finishes its free member's account.
 * @param {object} options.user - The user obtained via the Apple client-side API (may only exist on first-time login).
 * @returns {LoginResponse} Returns the user profile on success.
 */
export async function appleTokenIdLogin({
  api = serviceSingleton,
  authorizationCode,
  idToken,
  nonce,
  product = 'waybetter',
  prospectPhoneNumber,
  user: appleUser,
}) {
  const selectFullName = (user) => {
    if (!user?.name) return null;
    return {
      givenName: user.name.firstName,
      familyName: user.name.lastName,
    };
  };

  try {
    const user = await api
      .post(`auth/login/apple/token`, {
        json: {
          authorizationCode,
          identityToken: idToken,
          nonce,
          fullName: selectFullName(appleUser),
          ...(prospectPhoneNumber && { prospectPhoneNumber }),
        },
        headers: {
          ...DEFAULT_AUTH_HEADERS,
          'X-Product': product,
        },
      })
      .json();
    return { user };
  } catch (error) {
    logger.info(error, {
      message: 'Failed to authenticate user using Apple.',
    });
    return { error };
  }
}

/**
 * Performs a Facebook token login.
 *
 * @exports
 * @param {object} options - The parameters for the request.
 * @param {string} options.accessToken - The access token obtained via the Facebook client-side API.
 * @param {string} options.product - The product this login is for.
 * @param {object} [options.api] - Instance of API to user. Defaults to
 *   singleton.
 * @param {Function} [options.onError] - Optional error callback
 * @param {string} [options.prospectPhoneNumber] - The Supercharger pay flow's
 *   number (E.164): a new login finishes its free member's account.
 * @returns {LoginResponse} Returns the user profile on success.
 */
export async function facebookTokenLogin({
  accessToken,
  api = serviceSingleton,
  product = 'waybetter',
  onError,
  prospectPhoneNumber,
}) {
  try {
    const user = await api
      .post(`v2/auth/login/facebook/${HUB_APP_NAME}/token`, {
        json: {
          accessToken,
          ...(prospectPhoneNumber && { prospectPhoneNumber }),
        },
        headers: {
          ...DEFAULT_AUTH_HEADERS,
          'X-Product': product,
        },
      })
      .json();
    return { user };
  } catch (error) {
    logger.info(error, {
      message: 'Failed to authenticate user using Facebook.',
    });
    onError?.(error);
    return { error };
  }
}

/**
 * Calls to the auth service to {@link https://developers.google.com/identity/sign-in/web/backend-auth?hl=en|perform a verification of the Google ID token},
 * and creates a user if verification succeeds.
 *
 * @exports
 * @param {object} options - The parameters for the request.
 * @param {string} options.accessToken - The access token obtained via the Google client-side API.
 * @param {string} options.idToken - The ID token obtained via the Google client-side API.
 * @param {string} options.product - The product this login is for.
 * @param {object} [options.api] - Instance of API to user. Defaults to
 *   singleton.
 * @param {string} [options.prospectPhoneNumber] - The Supercharger pay flow's
 *   number (E.164): a new login finishes its free member's account.
 * @returns {LoginResponse} Returns the user profile on success.
 */
export async function googleTokenIdLogin({
  accessToken,
  idToken,
  api = serviceSingleton,
  product = 'waybetter',
  prospectPhoneNumber,
}) {
  try {
    const user = await api
      .post(`auth/login/google/token`, {
        json: {
          access_token: accessToken,
          id_token: idToken,
          ...(prospectPhoneNumber && { prospectPhoneNumber }),
        },
        headers: {
          ...DEFAULT_AUTH_HEADERS,
          'X-Product': product,
        },
      })
      .json();
    return { user };
  } catch (error) {
    logger.info(error, {
      message: 'Failed to authenticate user using Google.',
    });
    return { error };
  }
}

/**
 * Refreshes the user's JWT if the auth service session cookie is still valid.
 *
 * @exports
 * @param {object} [options={}]
 * @param {object} [options.api] - Instance of API to user. Defaults to
 *   singleton.
 * @returns {object} Refresh response {headers, user}
 */
export async function refreshLogin({ api = serviceSingleton } = {}) {
  const request = await api.post('auth/session/ping');
  const user = request.body;

  return { headers: request.headers, user };
}

/**
 * Checks the user's authentication status.
 *
 * @exports
 * @returns {boolean} Returns true if logged in and false when not logged in.
 */
export async function isLoggedIn() {
  try {
    const { user } = await refreshLogin();
    if (!user.id) return false;
    return user;
  } catch (error) {
    if (error.response?.statusCode != 401) {
      logger.info(error, { message: 'Failed to check login status.' });
    }
    return false;
  }
}

/**
 * Removes the user's session cookie therefore logging them out. If the user has
 * an invalid or missing session cookie then this will return false.
 *
 * @exports
 * @param {object} [options={}]
 * @param {object} [options.api] - Instance of API to user. Defaults to
 *   singleton.
 * @returns {boolean} True on success, false on possible failure.
 */
export async function logout({ api = serviceSingleton } = {}) {
  try {
    return await api.post('auth/logout');
  } catch (error) {
    logger.info(error, { message: 'Failed to log user out.' });
    return false;
  }
}

/**
 * Helper function to create the URL to redirect the user after a successful
 * authentication.
 *
 * @param {object} [defaultPath='/'] - The default path to return to.
 * @returns {string} The URL to redirect the user to.
 */
export const generateReturnUrl = (defaultPath = '/') => {
  const returnPath =
    window.sessionStorage.getItem(LOGIN_RETURN_PATH_KEY) || defaultPath;
  const url = new URL(returnPath, window.location.origin);
  return url.toString();
};

export const clearReturnPath = (window) =>
  window.sessionStorage.removeItem(LOGIN_RETURN_PATH_KEY);

/**
 * Takes a user object and POSTs it to the auth service which creates the user
 * and gives the client a JWT and session cookie.
 *
 * @param {object} user - The user data required to register a new user.
 * @param {string} user.firstName
 * @param {string} user.lastName
 * @param {string} user.email
 * @param {string} user.clearTextPassword
 * @param {object} [options={}]
 * @param {object} [options.api] - Instance of API to user. Defaults to
 *   singleton.
 * @param {object} [options.captchaToken] - Token for captcha verification.
 * @param {"recaptcha" | "turnstile"} [options.captchaType="recaptcha"]
 * @param {object} [options.product] - Product value to pass as a header. Used
 *   to identify user's first logged in product.
 * @returns {LoginResponse} Returns the user profile or false.
 */
export async function register(
  user,
  {
    api = serviceSingleton,
    captchaToken,
    captchaType = 'recaptcha',
    product,
  } = {}
) {
  try {
    const realUser = await api
      .post('auth/register', {
        json: {
          ...user,
          [captchaTokenField(captchaType)]: captchaToken,
        },
        headers: {
          ...DEFAULT_AUTH_HEADERS,
          'X-Product': defaultTo('waybetter', product),
        },
      })
      .json();

    return { user: realUser };
  } catch (error) {
    const parsed = isNetworkException(error)
      ? buildHumanReadableNetworkException()
      : parseNetworkErrorResponse(await error.response?.json());
    logger.info(error, {
      message: 'Failed to register user using email.',
      error: parsed,
    });

    return { error: parsed };
  }
}

/**
 * Extracts "set-cookie" headers from response object
 *
 * @param {object} resp
 * @returns {Array} - array of set-cookie headers
 */
const responseCookies = (resp) => {
  const cookies = [];
  resp.headers?.forEach((value, key) => {
    if (key === 'set-cookie') cookies.push(value);
  });
  return cookies;
};

/**.
 * Takes a prospect user object and POSTs it to the auth service which creates
 * the prospect user record.
 * 🚨 !! NOTE For security reasons, this should only be called server side !! 🚨
 *
 * @param {object} user - The user data required to register a new user.
 * @param {string} user.email - The email to create prospect for.
 * @param {object} options
 * @param {object} [options.api] - Instance of API to use. Defaults to
 *   singleton.
 * @param {object} options.ctx - The Express server context object
 * @param {object} options.product - Product value to pass as a header. Used
 *   to identify user's first logged in product.
 */
export async function preRegister(
  user,
  { api = serviceSingleton, ctx, product }
) {
  if (!isServer) {
    throw new Error('preRegister must be called server-side');
  }

  try {
    logAuthSession(`calling ${authServiceUrl}/auth/add-prospect`);
    const resp = await api.post(WB_AUTH_SERVICE_PRE_REGISTER_PATH, {
      json: pick(['email', 'firstName', 'lastName'], user),
      headers: {
        ...DEFAULT_AUTH_HEADERS,
        'X-Product': defaultTo('waybetter', product),
      },
    });

    // Copy the set-cookie header from the auth service response to the client
    if (ctx) {
      const respCookies = responseCookies(resp);
      logAuthSession(`Received prospect cookies ${respCookies}`);
      const cookies = switchAuthCookieToPublicDomain(ctx, respCookies);
      logAuthSession(`Setting client prospect cookies ${cookies}`);
      cookies.forEach((cookie) => ctx?.response.append('set-cookie', cookie));
    }

    const prospect = await resp.json();

    return prospect;
  } catch (error) {
    throw await transformPreRegisterError(error);
  }
}

/**.
 * Same functionality as preRegister except includes a recaptcha token for
 * protection. Intended to be used client side.
 *
 * @param {object} user - The user data required to register a new user.
 * @param {string} user.email - The email to create prospect for.
 * @param {object} options
 * @param {object} [options.api] - Instance of API to user. Defaults to.
 * @param {string} options.captchaToken - Token to be passed in request to
 *   auth service for recaptcha verification.
 * @param {"recaptcha" | "turnstile"} [options.captchaType="recaptcha"]
 * @param {object} options.product - Product value to pass as a header. Used
 *   to identify user's first logged in product.
 */
export async function protectedPreRegister(
  user,
  { api = serviceSingleton, captchaToken, captchaType = 'recaptcha', product }
) {
  try {
    return await api
      .post('auth/protected/pre-register', {
        json: {
          ...pick(['email', 'firstName', 'lastName'], user),
          [captchaTokenField(captchaType)]: captchaToken,
        },
        headers: {
          ...DEFAULT_AUTH_HEADERS,
          'X-Product': defaultTo('waybetter', product),
        },
      })
      .json();
  } catch (error) {
    throw await transformPreRegisterError(error);
  }
}

/**
 * Signs a visitor up for a host's open game from the host's text club page.
 * Called from the browser, so the auth service's Set-Cookie lands there:
 * signedIn true means the session is now her (user is her session user);
 * false means the number is a registered account's, and no session is
 * given. correction is "Fix it" only: then a prospect already signed up
 * with another number gets her number corrected; any other submit signs the
 * number up, whoever the browser was signed in as. buttonLabel is the
 * button she tapped, kept with her consent.
 *
 * @param {object} options
 * @param {number} options.hostUserId
 * @param {string} options.phoneNumber - E.164.
 * @param {string} [options.timeZone]
 * @param {string} options.smsConsentCopyVersion
 * @param {string} options.buttonLabel
 * @param {boolean} [options.correction] - True for "Fix it" only.
 * @param {string} options.captchaToken
 * @param {"recaptcha" | "turnstile"} [options.captchaType="recaptcha"]
 * @param {object} [options.api] - Instance of API to use. Defaults to
 *   singleton.
 * @returns {object} { gameId, phoneNumber, signedIn, user }. Throws on
 *   failure, with the response's status and error code (e.g. 409
 *   "phone_number_in_use") on the error.
 */
export async function startSuperchargerFreeEntry({
  hostUserId,
  buttonLabel,
  ...signUp
}) {
  return postSuperchargerSignUp(
    'startSuperchargerFreeEntry',
    'auth/supercharger/free-entry',
    { hostUserId, buttonLabel },
    signUp
  );
}

/**
 * A friend takes a paying member's free spot from the friend's page
 * (/invite/:token). Called from the browser, like
 * startSuperchargerFreeEntry, so the auth service's Set-Cookie lands there
 * and her "Fix it" can correct her number: signedIn true means the session
 * is now her. correction is "Fix it" only; any other submit takes the spot
 * by the number. marketingConsent / roundConsent are the page's two boxes.
 *
 * @param {object} options
 * @param {string} options.inviteToken
 * @param {string} options.phoneNumber - E.164.
 * @param {string} [options.timeZone]
 * @param {string} options.smsConsentCopyVersion
 * @param {boolean} options.marketingConsent
 * @param {boolean} options.roundConsent
 * @param {boolean} [options.correction] - True for "Fix it" only.
 * @param {string} options.captchaToken
 * @param {"recaptcha" | "turnstile"} [options.captchaType="recaptcha"]
 * @param {object} [options.api] - Instance of API to use. Defaults to
 *   singleton.
 * @returns {object} { gameId, phoneNumber, signedIn, user }. Throws on
 *   failure, with the response's status and error code (e.g. 422
 *   "notify_service_free_spot_taken", 409 "phone_number_in_use") on the
 *   error.
 */
export async function takeSuperchargerFreeSpot({
  inviteToken,
  marketingConsent,
  roundConsent,
  ...signUp
}) {
  return postSuperchargerSignUp(
    'takeSuperchargerFreeSpot',
    'auth/supercharger/free-spot',
    { inviteToken, marketingConsent, roundConsent },
    signUp
  );
}

// The two no-login Supercharger sign-ups above: fields is the route's own
// (host or invite token, and its consent fields).
async function postSuperchargerSignUp(
  name,
  path,
  fields,
  {
    phoneNumber,
    timeZone,
    smsConsentCopyVersion,
    correction,
    captchaToken,
    captchaType = 'recaptcha',
    api = serviceSingleton,
  }
) {
  try {
    return await api
      .post(path, {
        json: {
          ...fields,
          phoneNumber,
          timeZone,
          smsConsentCopyVersion,
          ...(correction ? { correction: true } : {}),
          [captchaTokenField(captchaType)]: captchaToken,
        },
        headers: DEFAULT_AUTH_HEADERS,
      })
      .json();
  } catch (error) {
    if (isNetworkException(error)) throw buildHumanReadableNetworkException();

    // The auth service's own refusals are { error: "<code>" }; notify's,
    // passed through, { error: { code, message } }.
    const errorBody = await error.response?.json().catch(() => null);
    const code = errorBody?.error?.code ?? errorBody?.error;
    const newError = new Error(`${name} failed: ${error.response?.status}`);
    newError.status = error.response?.status;
    newError.code = typeof code === 'string' ? code : undefined;
    throw newError;
  }
}

/**
 * The signed-in visitor's sign-up from a host's text club page, if any.
 *
 * @param {object} options
 * @param {number} options.hostUserId
 * @param {object} [options.api] - Instance of API to use. Defaults to
 *   singleton.
 * @returns {object|null} { gameId, phoneNumber }, or null when she has none
 *   (or isn't signed in, or the lookup failed).
 */
export async function currentSuperchargerFreeEntry({
  hostUserId,
  api = serviceSingleton,
}) {
  try {
    return await api
      .get('auth/supercharger/free-entry', {
        searchParams: { hostUserId },
        headers: DEFAULT_AUTH_HEADERS,
      })
      .json();
  } catch (error) {
    if (![401, 404].includes(error.response?.status))
      logger.info(error, {
        message: 'Failed to look up the text club sign-up.',
      });
    return null;
  }
}

/**
 * Fetches a new one time use auth token for a user. Useful for deep links to
 * auto log user into app.
 *
 * @param {object} user - The user to fetch auth token for.
 * @param {object} [options={}]
 * @param {object} [options.api] - Instance of API to user. Defaults to
 *   singleton.
 * @param {object} [options.product] - Product value to pass as a header. Used
 *   to identify user's first logged in product.
 * @returns {string} The auth token.
 */
export async function createAuthToken(
  user,
  { api = serviceSingleton, product } = {}
) {
  try {
    const { token } = await api
      .post('auth/token', {
        headers: {
          ...DEFAULT_AUTH_HEADERS,
          'X-Product': defaultTo('waybetter', product),
        },
      })
      .json();
    return token;
  } catch (error) {
    logger.error(`Failed to fetch auth token for ${user?.id}`, error);
  }
}

/**
 * Builds a custom error object containing a user facing error message about network not working.
 *
 * @returns {Error} Error object.
 */
const buildHumanReadableNetworkException = function () {
  const newError = new Error(MSG_NETWORK_ERROR);
  newError.name = 'NetworkError';
  newError.humanReadableErrorMessage = newError.message;
  return newError;
};

/**
 * Returns true if error was throws as result of a network problem.
 *
 * @param {Error} error
 * @returns {boolean} True if this is a network error.
 */
const isNetworkException = function (error) {
  return (
    error?.name === 'TimeoutError' ||
    (error?.name === 'TypeError' &&
      error?.message?.match(/network|fetch|request/))
  );
};

const transformPreRegisterError = async function (error) {
  logger.error(error, 'preRegister error');
  if (isNetworkException(error)) {
    const newError = buildHumanReadableNetworkException();
    logger.info(error, { message: newError.message });
    return newError;
  }

  const errorBody = await error.response?.json();
  const message = dotPath('error.message', errorBody);
  const newError = new Error(message);

  newError.name = dotPath('error.name', errorBody);
  newError.code = dotPath('error.code', errorBody);
  newError.status = dotPath('error.status', errorBody);
  newError.errors = dotPath('error.errors', errorBody);

  logger.info(newError, { message });
  return newError;
};

/**
 * Call the API to log out the user session server-side,
 * as well as clearing related client-side data.
 *
 * 🚨 !! NOTE Regarding Set-Cookie Headers !! 🚨 .
 *
 * There is a limitation with the Fetch standard
 * (see {@link https://stackoverflow.com/questions/63204093/how-to-get-set-multiple-set-cookie-headers-using-fetch-api|this SO thread})
 * which results in KY being unable to read all the "Set-Cookie"
 * headers sent by the auth service. This is a problem, because
 * the auth service sends one header to remove the JWT, and another
 * to reset the session, and Fetch does a weird merge of the
 * multiple instances of the Set-Cookie headers into one in a way
 * that not all the values are parse-able.
 *
 * This means that if this code is executed on the server,
 * then the step to copy of the Set-Cookie to the client may not
 * include the cookie to reset the session, which leaves the
 * old cookie in the browser active.
 *
 * Components that wish to truly log out the user will need
 * to call logout() client-side.
 *
 * @param {object} opts
 * @param {object} opts.ctx - Koa context.
 * @param {Function} opts.dispatch - Redux dispatch.
 * @param {object} opts.graphqlClient - GraphQL client instance.
 * @returns {object} Logout response headers
 */
export const logoutAndClearClientState = async ({
  ctx,
  dispatch,
  graphqlClient,
}) => {
  const { headers } = await logout();

  // Copy logout headers to the response for the client
  ctx &&
    ctx.response.set(
      'set-cookie',
      switchAuthCookieToPublicDomain(ctx, headers['set-cookie'])
    );

  clearClientState({ dispatch, graphqlClient });

  return headers;
};

export const clearClientState = ({ dispatch, graphqlClient }) => {
  if (typeof window !== 'undefined') {
    // Clear sessionStorage to clean user session data.
    window.sessionStorage.clear();
    clearLoggedUser();
  }

  graphqlClient.resetStore();
  dispatch(sessionRemoval());
};
